"use client";

import NumberCard from "@/components/Cards/NumberCard";
import CountrySelectorGenerator from "@/components/NumberGenerator/CountrySelectorGenerator";
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Hyphenated } from "@/utils/Hyphenated";
import { useLiveNumbers } from "@/lib/useLiveNumbers";
import AllPrivateNumbersList from "@/components/ClientComponents/AllPrivateNumbersList";

export default function RentPrivateNumberCountryClient({ country, phoneNumbers, countryName, countries }) {
  const [selectedCountry, setSelectedCountry] = useState(country);
  const router = useRouter();

  // Overlay the build-time list with the current inventory (fallback: static).
  const live = useLiveNumbers(country?.country_alpha_code);
  const currentNumbers = live ? live.privateNumbers : phoneNumbers;

  // Filter available numbers
  const filterAvailableNumbers = currentNumbers.filter(
    (number) => number.status.toLowerCase() === "available"
  );

  // Handle country change - navigate to selected country page
  const handleCountryChange = (newCountry) => {
    if (newCountry && newCountry.country_alpha_code !== country.country_alpha_code) {
      const countrySlug = Hyphenated(newCountry.country_name);
      router.push(`/rent-private-number/${countrySlug}`);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-tr to-[#090EDB] from-[#E73DC4] rounded-2xl flex lg:flex-row py-20 flex-col items-start justify-between space-x-1 space-y-4 lg:space-y-0">
        <div className="w-full flex items-center flex-col space-y-5 text-center">
          <h1 className="lg:w-[90%] w-[98%] font-bold text-3xl md:text-5xl text-white">
            Rent a Private {countryName} Phone Number — Dedicated for Your Use Only
          </h1>

          <h2 className="lg:w-[90%] w-[98%] text-gray-300 text-sm md:text-base font-medium ">
            Rent a private {countryName} phone number that is exclusively yours — never shared, never reassigned. Whether you need a long term phone number in {countryName} for business, a personal virtual number for online accounts, or a dedicated SMS number for verification, we have you covered. Choose your {countryName} virtual number, rent it monthly or long-term, and keep it as long as you need. Your number, your privacy, your control.
          </h2>

          {/* Country selector dropdown */}
          {countries && countries.length > 0 && (
            <div className="w-[95%] md:w-[80%] max-w-md">
              <label className="block text-white/90 text-sm font-semibold mb-2">
                Change Country
              </label>
              <CountrySelectorGenerator
                data={countries}
                value={selectedCountry}
                handleMenuItemClick={handleCountryChange}
              />
            </div>
          )}

          <h2 className="lg:w-[90%] w-[98%] text-gray-300 text-sm md:text-base font-medium ">
            💯 Guaranteed to work on all platforms, or your money back.
          </h2>
        </div>
      </div>

      <h2 className="pt-14 text-gray-600 font-medium text-lg md:text-xl">
        {filterAvailableNumbers.length > 0
          ? `${filterAvailableNumbers.length} ${countryName} Phone Numbers Available to Rent`
          : `Sorry! No Numbers Are Available for ${countryName}`}
      </h2>

      <div className="gap-4 w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {currentNumbers?.map((number) => (
          <NumberCard
            isPrivate={true}
            key={number.id}
            country={`${number.countries.country_flag} ${number.countries.country_name}`}
            status={number.status.toLowerCase()}
            number={`+${number.countries.country_tel_code}-${number.number}`}
            href={`/account/rent-number?pn=${number.id}`}
            price={number.week_price}
          />
        ))}
      </div>

      {/* Numbers work for any country — never leave the visitor stranded
          when local stock runs out. */}
      <AllPrivateNumbersList guaranteeCountry={countryName} />
    </>
  );
}

